import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { reduxForm, formValueSelector, change } from 'redux-form';
import { push } from 'connected-react-router';
import moment from 'moment';

import { getSelectOptions } from 'src/reducers/selectOptions';
import { scrollToTop } from 'src/utils/Utils';
import { INCOME_TAX_DECLARATION_LIST } from 'src/constants/EndpointUrls';
import {
  createIncomeTaxDeclaration,
  checkIncomeTaxDeclarationExists,
  checkIncomeTaxDeclarationUnconfirmedPayrolls,
  setCreatingStatus,
  CREATE_FORM_NAME,
  getIsCreating
} from 'src/reducers/reports/incomeTaxDeclarations';
import { fetchIncomeTaxDeclarationFormOptions } from 'src/reducers/fetchSelectOptions';

import CreateDeclarationForm from '../components/CreateDeclarationForm';

const DECLARATION_TYPE_SUPPLEMENT = 'C';

const selector = formValueSelector(CREATE_FORM_NAME);

const selectPeriodType = state => selector(state, 'periodType');
const selectYear = state => selector(state, 'year');
const selectPeriodMonth = state => selector(state, 'periodMonth');
const selectPeriodQuarter = state => selector(state, 'periodQuarter');
const selectBranchId = state => selector(state, 'branchId');
const selectIncludeAppendix051 = state => selector(state, 'includeAppendix051');

const selectBranchesForDeclaration = state => getSelectOptions(state, 'branchesForDeclaration') || [];
const selectPeriodTypes = state => getSelectOptions(state, 'periodTypes') || [];
const selectFormCode = state => getSelectOptions(state, 'formCode') || '';

const CreateDeclarationFormContainer = props => {
  const dispatch = useDispatch();

  useEffect(
    () => {
      dispatch(fetchIncomeTaxDeclarationFormOptions());
    },
    [dispatch]
  );

  const periodType = useSelector(selectPeriodType);
  const year = useSelector(selectYear);
  const periodMonth = useSelector(selectPeriodMonth);
  const periodQuarter = useSelector(selectPeriodQuarter);
  const selectedBranchId = useSelector(selectBranchId);
  const includeAppendix051 = useSelector(selectIncludeAppendix051);
  const isCreating = useSelector(getIsCreating);
  const [isExistingModalOpen, setIsExistingModalOpen] = useState(false);
  const [isUnconfirmedModalOpen, setIsUnconfirmedModalOpen] = useState(false);
  const [unconfirmedPaymentMonths, setUnconfirmedPaymentMonths] = useState([]);
  const [formErrors, setFormErrors] = useState([]);
  const pendingValuesRef = useRef(null);

  const branchesForDeclaration = useSelector(selectBranchesForDeclaration);
  const periodTypes = useSelector(selectPeriodTypes);
  const formCode = useSelector(selectFormCode);

  const branchOptions = useMemo(
    () =>
      branchesForDeclaration.map(branch => ({
        value: branch.value,
        label: branch.label
      })),
    [branchesForDeclaration]
  );

  const selectedBranch = useMemo(
    () => {
      if (!selectedBranchId) return null;
      return branchesForDeclaration.find(b => b.value === selectedBranchId) || null;
    },
    [branchesForDeclaration, selectedBranchId]
  );

  const canIncludeAppendix = Boolean(selectedBranch && selectedBranch.dependentBranches && selectedBranch.dependentBranches.length > 0);

  useEffect(
    () => {
      dispatch(change(CREATE_FORM_NAME, 'companyId', selectedBranch ? selectedBranch.companyId : null));
    },
    [selectedBranch, dispatch]
  );

  useEffect(
    () => {
      if (!canIncludeAppendix && includeAppendix051) {
        dispatch(change(CREATE_FORM_NAME, 'includeAppendix051', false));
      }
    },
    [canIncludeAppendix, includeAppendix051, dispatch]
  );

  useEffect(
    () => {
      setFormErrors([]);
    },
    [year, periodType, periodMonth, periodQuarter, selectedBranchId]
  );

  const handleCancel = useCallback(
    () => {
      dispatch(push(INCOME_TAX_DECLARATION_LIST));
    },
    [dispatch]
  );

  const handleChangeDeclarationName = useCallback(
    value => {
      dispatch(change(CREATE_FORM_NAME, 'declarationName', value));
    },
    [dispatch]
  );

  const runCreateWithLoading = useCallback(
    async values => {
      dispatch(setCreatingStatus(true));
      try {
        const errors = await dispatch(createIncomeTaxDeclaration(values));
        if (errors) {
          setFormErrors(errors);
          scrollToTop();
        }
      } finally {
        dispatch(setCreatingStatus(false));
      }
    },
    [dispatch]
  );

  const runExistingCheck = useCallback(
    async values => {
      const exists = await dispatch(checkIncomeTaxDeclarationExists(values));
      if (exists) {
        pendingValuesRef.current = values;
        setIsExistingModalOpen(true);
        return;
      }
      runCreateWithLoading(values);
    },
    [dispatch, runCreateWithLoading]
  );

  const resolveValues = useCallback(
    values => ({
      ...values,
      branchId: selectedBranch ? selectedBranch.clientBranchId : -1,
      companyId: selectedBranch ? selectedBranch.companyId : null
    }),
    [selectedBranch]
  );

  const handleSubmit = useCallback(
    async values => {
      setFormErrors([]);
      const resolved = resolveValues(values);
      const payrollMonths = await dispatch(checkIncomeTaxDeclarationUnconfirmedPayrolls(resolved));

      const missingItems = payrollMonths.filter(item => item.status === 'missing');
      if (missingItems.length > 0) {
        const errors = await dispatch(createIncomeTaxDeclaration(resolved));
        if (errors) {
          setFormErrors(errors);
          scrollToTop();
        }
        return;
      }

      const unconfirmedItems = payrollMonths.filter(item => item.status === 'unconfirmed');
      if (unconfirmedItems.length > 0) {
        pendingValuesRef.current = resolved;
        setUnconfirmedPaymentMonths(unconfirmedItems);
        setIsUnconfirmedModalOpen(true);
        return;
      }
      runExistingCheck(resolved);
    },
    [dispatch, resolveValues, runExistingCheck]
  );

  const handleExistingModalCancel = useCallback(() => {
    pendingValuesRef.current = null;
    setIsExistingModalOpen(false);
  }, []);

  const handleExistingModalConfirm = useCallback(
    async () => {
      setIsExistingModalOpen(false);
      if (pendingValuesRef.current) {
        const values = pendingValuesRef.current;
        pendingValuesRef.current = null;
        await runCreateWithLoading(values);
      }
    },
    [runCreateWithLoading]
  );

  const handleUnconfirmedModalCancel = useCallback(() => {
    pendingValuesRef.current = null;
    setUnconfirmedPaymentMonths([]);
    setIsUnconfirmedModalOpen(false);
  }, []);

  const handleUnconfirmedModalConfirm = useCallback(
    () => {
      const values = pendingValuesRef.current;
      setIsUnconfirmedModalOpen(false);
      if (values) {
        runExistingCheck(values);
      }
    },
    [runExistingCheck]
  );

  return (
    <CreateDeclarationForm
      {...props}
      periodType={periodType}
      year={year}
      periodMonth={periodMonth}
      periodQuarter={periodQuarter}
      branchOptions={branchOptions}
      selectedBranch={selectedBranch}
      periodTypes={periodTypes}
      formCode={formCode}
      canIncludeAppendix={canIncludeAppendix}
      isCreating={isCreating}
      formErrors={formErrors}
      isExistingModalOpen={isExistingModalOpen}
      isUnconfirmedModalOpen={isUnconfirmedModalOpen}
      unconfirmedPaymentMonths={unconfirmedPaymentMonths}
      onExistingModalCancel={handleExistingModalCancel}
      onExistingModalConfirm={handleExistingModalConfirm}
      onUnconfirmedModalCancel={handleUnconfirmedModalCancel}
      onUnconfirmedModalConfirm={handleUnconfirmedModalConfirm}
      onCancel={handleCancel}
      onChangeDeclarationName={handleChangeDeclarationName}
      onSubmitForm={handleSubmit}
    />
  );
};

export default reduxForm({
  form: CREATE_FORM_NAME,
  initialValues: {
    branchId: -1,
    periodType: 'monthly',
    year: moment().year(),
    periodMonth: moment().month() + 1,
    periodQuarter: Math.ceil((moment().month() + 1) / 3),
    declarationType: DECLARATION_TYPE_SUPPLEMENT,
    includeAppendix051: false
  }
})(CreateDeclarationFormContainer);
